import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApiGet, adminApiPut, adminApiPost, adminApiDelete } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  ArrowCounterClockwise,
  ArrowRight,
  ArrowSquareOut,
  Buildings,
  CaretDown,
  CheckCircle,
  CircleNotch,
  Clock,
  Envelope,
  Eye,
  Funnel,
  Globe,
  MagnifyingGlass,
  MapPin,
  PencilSimple,
  Shield,
  Trash,
  UsersThree,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
const AdminPartners = () => {
  const [activeTab, setActiveTab] = useState('applications');
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [partners, setPartners] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [publishedCount, setPublishedCount] = useState(0);
  const [unpublishedCount, setUnpublishedCount] = useState(0);
  
  const [statusFilter, setStatusFilter] = useState('');
  const [publishedFilter, setPublishedFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'edit', 'create'
  const [processing, setProcessing] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);

  useEffect(() => {
    if (activeTab === 'applications') {
      fetchApplications();
    } else {
      fetchPartners();
    }
  }, [activeTab, statusFilter, publishedFilter, searchTerm]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const data = await adminApiGet(`/api/admin/partner-applications?${params}`);
      let filtered = data.applications || [];
      
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(app => 
          app.business_name?.toLowerCase().includes(term) ||
          app.contact_email?.toLowerCase().includes(term)
        );
      }
      
      setApplications(filtered);
      setStatusCounts(data.status_counts || {});
    } catch (err) {
      console.error('Error fetching applications:', err);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (publishedFilter) params.append('published', publishedFilter);

      const data = await adminApiGet(`/api/admin/partners?${params}`);

      let filtered = data.partners || [];
      
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(p => 
          p.business_name?.toLowerCase().includes(term) ||
          p.display_name?.toLowerCase().includes(term)
        );
      }
      
      setPartners(filtered);
      setPublishedCount(data.published_count || 0);
      setUnpublishedCount(data.unpublished_count || 0);
    } catch (err) {
      console.error('Error fetching partners:', err);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId, status, adminNotes = '') => {
    setProcessing(true);
    try {
      await adminApiPut(`/api/admin/partner-applications/${applicationId}/status`, { 
        status, 
        admin_notes: adminNotes 
      });
      
      toast.success(`Application ${status}`);
      fetchApplications();
      setShowModal(false);
      setSelectedApplication(null);
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error(err.message || 'Failed to update status');
    } finally {
      setProcessing(false);
    }
  };

  const createPartnerFromApplication = async (applicationId, displayName, shortDescription) => {
    setProcessing(true);
    try {
      await adminApiPost(`/api/admin/partners`, {
        application_id: applicationId,
        display_name: displayName,
        short_description: shortDescription
      });
      
      toast.success('Partner published to Find a Partner directory!');
      fetchApplications();
      fetchPartners();
      setShowModal(false);
      setSelectedApplication(null);
    } catch (err) {
      console.error('Error creating partner:', err);
      toast.error(err.message || 'Failed to create partner');
    } finally {
      setProcessing(false);
    }
  };

  const updatePartner = async (partnerId, updates) => {
    setProcessing(true);
    try {
      await adminApiPut(`/api/admin/partners/${partnerId}`, updates);
      
      toast.success('Partner updated');
      fetchPartners();
      setShowModal(false);
      setSelectedPartner(null);
    } catch (err) {
      console.error('Error updating partner:', err);
      toast.error(err.message || 'Failed to update partner');
    } finally {
      setProcessing(false);
    }
  };

  const togglePublish = async (partnerId) => {
    try {
      await adminApiPut(`/api/admin/partners/${partnerId}/publish`, {});
      fetchPartners();
    } catch (err) {
      console.error('Error toggling publish:', err);
      toast.error(err.message || 'Failed to toggle publish status');
    }
  };

  const suspendPartner = async (partnerId, suspend) => {
    const action = suspend ? 'suspend' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${action} this partner?`)) return;
    
    try {
      await adminApiPut(`/api/admin/partners/${partnerId}/suspend`, { suspended: suspend });
      
      toast.success(`Partner ${suspend ? 'suspended' : 'reactivated'} successfully`);
      fetchPartners();
    } catch (err) {
      console.error('Error suspending partner:', err);
      toast.error(err.message || `Failed to ${action} partner`);
    }
  };

  const deletePartner = async (partnerId) => {
    if (!window.confirm('Are you sure you want to permanently delete this partner? This action cannot be undone.')) return;
    
    try {
      await adminApiDelete(`/api/admin/partners/${partnerId}`);
      
      toast.success('Partner deleted successfully');
      fetchPartners();
    } catch (err) {
      console.error('Error deleting partner:', err);
      toast.error(err.message || 'Failed to delete partner');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: Clock },
      pending_subscription: { color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', icon: WarningCircle },
      approved: { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: CheckCircle },
      rejected: { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: XCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border ${badge.color}`}>
        <Icon size={12} />
        {status.replace('_', ' ')}
      </span>
    );
  };

  const saveApplicationEdit = async () => {
    if (!editingApplication) return;
    
    setProcessing(true);
    try {
      await adminApiPut(`/api/admin/partner-applications/${selectedApplication.id}`, editingApplication);
      
      toast.success('Application updated successfully');
      fetchApplications();
      setEditingApplication(null);
      setModalMode('view');
      
      // Refresh the selected application
      const updated = { ...selectedApplication, ...editingApplication };
      setSelectedApplication(updated);
    } catch (err) {
      console.error('Error updating application:', err);
      toast.error(err.message || 'Failed to update application');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Partner Management</h1>
            <p className="text-[#A0AEC0] mt-1">Review applications and manage partner profiles</p>
          </div>
          <button
            onClick={() => {
              setSelectedPartner(null);
              setSelectedApplication(null);
              setModalMode('create');
              setShowModal(true);
            }}
            className="px-4 py-2 bg-[#6366F1] text-white rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
          >
            <Buildings size={18} />
            Create Partner
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'applications'
                ? 'bg-[#6366F1] text-white'
                : 'glass text-[#A0AEC0] hover:text-white'
            }`}
          >
            Applications
            {statusCounts.pending > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded-full">
                {statusCounts.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('partners')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'partners'
                ? 'bg-[#6366F1] text-white'
                : 'glass text-[#A0AEC0] hover:text-white'
            }`}
          >
            Partners
            <span className="ml-2 px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">
              {publishedCount}
            </span>
          </button>
        </div>

        {/* Filters */}
        <div className="glass rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px] relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={activeTab === 'applications' ? 'Search by business name or email...' : 'Search partners...'}
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
              />
            </div>

            {activeTab === 'applications' ? (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
                style={{ colorScheme: 'dark' }}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending ({statusCounts.pending || 0})</option>
                <option value="pending_subscription">Pending Subscription ({statusCounts.pending_subscription || 0})</option>
                <option value="approved">Approved ({statusCounts.approved || 0})</option>
                <option value="rejected">Rejected ({statusCounts.rejected || 0})</option>
              </select>
            ) : (
              <select
                value={publishedFilter}
                onChange={(e) => setPublishedFilter(e.target.value)}
                className="px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
                style={{ colorScheme: 'dark' }}
              >
                <option value="">All Partners</option>
                <option value="true">Published ({publishedCount})</option>
                <option value="false">Unpublished ({unpublishedCount})</option>
              </select>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={40} className="text-[#6366F1] animate-spin" />
          </div>
        ) : activeTab === 'applications' ? (
          /* Applications List */
          <div className="space-y-3">
            {applications.length === 0 ? (
              <div className="text-center py-20 text-[#A0AEC0]">
                No applications found
              </div>
            ) : (
              applications.map((app) => (
                <motion.div
                  key={app.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass rounded-xl p-4 hover:border-[#6366F1]/30 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center flex-shrink-0">
                      <Buildings size={24} className="text-white" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-white truncate">{app.business_name}</h3>
                        {getStatusBadge(app.status)}
                        {!app.is_partner_pro && (
                          <span className="px-2 py-0.5 text-xs bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded">
                            No Partner Pro
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-[#A0AEC0]">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {app.city}, {app.country}
                        </span>
                        <span className="flex items-center gap-1">
                          <Envelope size={14} />
                          {app.contact_email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {new Date(app.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <a
                        href={app.website_or_linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                        title="View website"
                      >
                        <ArrowSquareOut size={16} className="text-[#A0AEC0]" />
                      </a>
                      <button
                        onClick={() => {
                          setSelectedApplication(app);
                          setModalMode('view');
                          setShowModal(true);
                        }}
                        className="px-3 py-1.5 rounded-lg bg-[#6366F1] text-white text-sm font-medium hover:opacity-90 transition-opacity"
                      >
                        Review
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        ) : (
          /* Partners List */
          <div className="space-y-3">
            {partners.length === 0 ? (
              <div className="text-center py-20 text-[#A0AEC0]">
                No partners found
              </div>
            ) : (
              partners.map((partner) => (
                <motion.div
                  key={partner.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass rounded-xl p-4 hover:border-[#6366F1]/30 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center flex-shrink-0">
                      <Buildings size={24} className="text-white" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-white truncate">
                          {partner.display_name || partner.business_name}
                        </h3>
                        {partner.published ? (
                          <span className="px-2 py-0.5 text-xs bg-green-500/10 text-green-400 border border-green-500/20 rounded">
                            Published
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs bg-gray-500/10 text-gray-400 border border-gray-500/20 rounded">
                            Unpublished
                          </span>
                        )}
                        {!partner.is_partner_pro && (
                          <span className="px-2 py-0.5 text-xs bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded">
                            No Partner Pro
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-[#A0AEC0]">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {partner.city}, {partner.country}
                        </span>
                        <span className="truncate max-w-[300px]">
                          {partner.short_description}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => togglePublish(partner.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          partner.published
                            ? 'bg-gray-500/10 text-gray-400 hover:bg-gray-500/20'
                            : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                        }`}
                        disabled={!partner.is_partner_pro && !partner.published}
                        title={!partner.is_partner_pro ? 'Partner Pro required to publish' : ''}
                      >
                        {partner.published ? 'Unpublish' : 'Publish'}
                      </button>
                      <button
                        onClick={() => suspendPartner(partner.id, !partner.suspended)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          partner.suspended
                            ? 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20'
                            : 'bg-orange-500/10 text-orange-400 hover:bg-orange-500/20'
                        }`}
                        title={partner.suspended ? 'Reactivate partner' : 'Suspend partner temporarily'}
                      >
                        {partner.suspended ? 'Reactivate' : 'Suspend'}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPartner(partner);
                          setModalMode('edit');
                          setShowModal(true);
                        }}
                        className="p-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                      >
                        <PencilSimple size={16} className="text-[#A0AEC0]" />
                      </button>
                      <button
                        onClick={() => deletePartner(partner.id)}
                        className="p-2 rounded-lg glass hover:bg-red-500/20 transition-colors"
                        title="Delete partner"
                      >
                        <Trash size={16} className="text-red-400" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                {/* EDIT MODE */}
                {modalMode === 'edit' && editingApplication && selectedApplication && (
                  <>
                    <h2 className="text-xl font-bold text-white mb-4">Review Application</h2>
                    
                    <div className="space-y-4 mb-6">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Business Name</label>
                          <input
                            type="text"
                            value={editingApplication.business_name || ''}
                            onChange={(e) => setEditingApplication({...editingApplication, business_name: e.target.value})}
                            className="w-full px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Contact Email</label>
                          <input
                            type="email"
                            value={editingApplication.contact_email || ''}
                            onChange={(e) => setEditingApplication({...editingApplication, contact_email: e.target.value})}
                            className="w-full px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Country</label>
                          <input
                            type="text"
                            value={editingApplication.country || ''}
                            onChange={(e) => setEditingApplication({...editingApplication, country: e.target.value})}
                            className="w-full px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">City</label>
                          <input
                            type="text"
                            value={editingApplication.city || ''}
                            onChange={(e) => setEditingApplication({...editingApplication, city: e.target.value})}
                            className="w-full px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Website / LinkedIn</label>
                        <input
                          type="url"
                          value={editingApplication.website_or_linkedin || ''}
                          onChange={(e) => setEditingApplication({...editingApplication, website_or_linkedin: e.target.value})}
                          className="w-full px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white"
                        />
                      </div>
                      
                      <div className="bg-[rgba(99,102,241,0.1)] border border-[#6366F1]/30 rounded-lg p-3">
                        <label className="text-xs text-[#6366F1] font-medium block mb-1">Man & Machine User ID</label>
                        <input
                          type="text"
                          value={editingApplication.provided_user_id || ''}
                          onChange={(e) => setEditingApplication({...editingApplication, provided_user_id: e.target.value})}
                          placeholder="Enter user ID to verify Partner Pro status"
                          className="w-full px-3 py-2 bg-[rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white font-mono text-sm"
                        />
                        <p className="text-xs text-[#A0AEC0] mt-1">Save to verify Partner Pro subscription status</p>
                      </div>
                      
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">About Business</label>
                        <textarea
                          value={editingApplication.about_business || ''}
                          onChange={(e) => setEditingApplication({...editingApplication, about_business: e.target.value})}
                          rows={4}
                          className="w-full px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-3 pt-4 border-t border-[rgba(255,255,255,0.1)]">
                      <button
                        onClick={saveApplicationEdit}
                        disabled={processing}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                      >
                        {processing ? <CircleNotch size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                        Save Changes
                      </button>
                      <button
                        onClick={() => {
                          setEditingApplication(null);
                          setModalMode('view');
                        }}
                        className="px-4 py-2 bg-[rgba(255,255,255,0.1)] text-white rounded-lg font-medium hover:bg-[rgba(255,255,255,0.15)]"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
                
                {/* VIEW MODE */}
                {modalMode === 'view' && selectedApplication && (
                  <>
                    <h2 className="text-xl font-bold text-white mb-4">Review Application</h2>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[#A0AEC0]">Status:</span>
                        {getStatusBadge(selectedApplication.status)}
                        {!selectedApplication.is_partner_pro && (
                          <span className="px-2 py-1 text-xs bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded flex items-center gap-1">
                            <WarningCircle size={12} />
                            No Partner Pro subscription
                          </span>
                        )}
                      </div>
                      
                      {/* User ID Section - Important for verification */}
                      <div className="bg-[rgba(99,102,241,0.1)] border border-[#6366F1]/30 rounded-lg p-3">
                        <label className="text-xs text-[#6366F1] font-medium">Man & Machine User ID</label>
                        <div className="flex items-center gap-2 mt-1">
                          {selectedApplication.provided_user_id ? (
                            <>
                              <code className="text-white bg-[rgba(0,0,0,0.3)] px-2 py-1 rounded text-sm font-mono">
                                {selectedApplication.provided_user_id}
                              </code>
                              {selectedApplication.is_partner_pro ? (
                                <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded flex items-center gap-1">
                                  <CheckCircle size={12} />
                                  Partner Pro Verified
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded flex items-center gap-1">
                                  <XCircle size={12} />
                                  Not Partner Pro
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-orange-400 text-sm">
                              No User ID provided - cannot verify Partner Pro status
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-gray-500">Business Name</label>
                          <p className="text-white">{selectedApplication.business_name}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Contact Email</label>
                          <p className="text-white">{selectedApplication.contact_email}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Location</label>
                          <p className="text-white">{selectedApplication.city}, {selectedApplication.country}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Website/LinkedIn</label>
                          <a href={selectedApplication.website_or_linkedin} target="_blank" rel="noopener noreferrer" className="text-[#6366F1] hover:underline">
                            {selectedApplication.website_or_linkedin}
                          </a>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-gray-500">Service Locations</label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedApplication.service_locations?.map((loc) => (
                            <span key={loc} className="px-2 py-1 text-xs bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] rounded">
                              {loc}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-gray-500">Industries Served</label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedApplication.industries_served?.map((ind) => (
                            <span key={ind} className="px-2 py-1 text-xs bg-[#6366F1]/10 text-[#6366F1] rounded">
                              {ind}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-gray-500">Services Offered</label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedApplication.services_offered?.map((svc) => (
                            <span key={svc} className="px-2 py-1 text-xs bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] rounded">
                              {svc}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-gray-500">About Business</label>
                        <p className="text-white text-sm mt-1 whitespace-pre-wrap">
                          {selectedApplication.about_business}
                        </p>
                      </div>
                      
                      {/* Submitted Date */}
                      <div className="text-xs text-gray-500">
                        Submitted: {selectedApplication.created_at ? new Date(selectedApplication.created_at).toLocaleString() : 'Unknown'}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-[rgba(255,255,255,0.1)]">
                      {/* PencilSimple Button - Always available */}
                      <button
                        onClick={() => {
                          setEditingApplication({
                            business_name: selectedApplication.business_name,
                            contact_email: selectedApplication.contact_email,
                            website_or_linkedin: selectedApplication.website_or_linkedin,
                            country: selectedApplication.country,
                            city: selectedApplication.city,
                            about_business: selectedApplication.about_business,
                            provided_user_id: selectedApplication.provided_user_id || ''
                          });
                          setModalMode('edit');
                        }}
                        className="px-4 py-2 bg-[#6366F1] text-white rounded-lg font-medium hover:opacity-90 flex items-center gap-2"
                      >
                        <PencilSimple size={16} />
                        PencilSimple Application
                      </button>
                      
                      {selectedApplication.status === 'pending' || selectedApplication.status === 'pending_subscription' ? (
                        <>
                          <button
                            onClick={() => {
                              if (!selectedApplication.is_partner_pro) {
                                if (!window.confirm('WARNING: This applicant does not have Partner Pro subscription. Are you sure you want to approve anyway?')) {
                                  return;
                                }
                              }
                              updateApplicationStatus(selectedApplication.id, 'approved');
                            }}
                            disabled={processing}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            <CheckCircle size={16} />
                            Approve
                          </button>
                          <button
                            onClick={() => updateApplicationStatus(selectedApplication.id, 'rejected')}
                            disabled={processing}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                          >
                            <XCircle size={16} />
                            Reject
                          </button>
                        </>
                      ) : selectedApplication.status === 'approved' ? (
                        <button
                          onClick={() => createPartnerFromApplication(
                            selectedApplication.id,
                            selectedApplication.business_name,
                            selectedApplication.about_business?.substring(0, 200)
                          )}
                          disabled={processing}
                          className="px-4 py-2 bg-[#14F195] text-black rounded-lg font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                        >
                          <Globe size={16} />
                          Publish to Find a Partner
                        </button>
                      ) : null}
                      
                      <button
                        onClick={() => {
                          setShowModal(false);
                          setSelectedApplication(null);
                          setEditingApplication(null);
                          setModalMode('view');
                        }}
                        className="px-4 py-2 glass text-white rounded-lg font-medium hover:bg-[rgba(255,255,255,0.1)]"
                      >
                        Close
                      </button>
                    </div>
                  </>
                )}

                {selectedPartner && modalMode === 'edit' && (
                  <PartnerEditForm
                    partner={selectedPartner}
                    onSave={(updates) => updatePartner(selectedPartner.id, updates)}
                    onCancel={() => {
                      setShowModal(false);
                      setSelectedPartner(null);
                    }}
                    processing={processing}
                  />
                )}

                {modalMode === 'create' && !selectedPartner && !selectedApplication && (
                  <PartnerCreateForm
                    onSave={async (partnerData) => {
                      setProcessing(true);
                      try {
                        await adminApiPost('/api/admin/partners/manual', partnerData);
                        
                        toast.success('Partner created successfully!');
                        fetchPartners();
                        setShowModal(false);
                        setActiveTab('partners');
                      } catch (err) {
                        console.error('Error creating partner:', err);
                        toast.error(err.message || 'Failed to create partner');
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    onCancel={() => setShowModal(false)}
                    processing={processing}
                  />
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Partner Create Form Component (Manual Creation)
const PartnerCreateForm = ({ onSave, onCancel, processing }) => {
  const [formData, setFormData] = useState({
    business_name: '',
    display_name: '',
    website_or_linkedin: '',
    country: '',
    city: '',
    service_locations: [],
    industries_served: [],
    services_offered: [],
    short_description: '',
    contact_email: '',
    contact_phone: '',
    user_id: '',
    published: false
  });

  const serviceLocations = [
    "Australia", "New Zealand", "United States", "Canada", "United Kingdom",
    "Germany", "France", "Netherlands", "Singapore", "Hong Kong",
    "Japan", "South Korea", "India", "Philippines", "Global (Remote)"
  ];

  const industries = [
    "Healthcare", "Finance & Banking", "Real Estate", "Legal", "Education",
    "E-commerce & Retail", "Manufacturing", "Professional Services",
    "Technology & SaaS", "Marketing & Advertising", "Hospitality & Tourism",
    "Construction", "Logistics & Supply Chain", "Government & Public Sector"
  ];

  const services = [
    "AI Strategy & Consulting", "Voice Agent Development", "Chatbot Development",
    "Process Automation", "AI Integration", "Custom AI Solutions",
    "AI Training & Workshops", "Managed AI Services", "Data & Analytics"
  ];

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.business_name || !formData.contact_email) {
      alert('Business name and contact email are required');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto">
      <h2 className="text-xl font-bold text-white mb-4">Create Partner</h2>
      
      <div className="space-y-4 mb-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">Business Name *</label>
            <input
              type="text"
              required
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
              placeholder="Company name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">Display Name</label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
              placeholder="Display name (optional)"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">Website or LinkedIn *</label>
          <input
            type="url"
            required
            value={formData.website_or_linkedin}
            onChange={(e) => setFormData({ ...formData, website_or_linkedin: e.target.value })}
            className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
            placeholder="https://..."
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">Country *</label>
            <input
              type="text"
              required
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
              placeholder="Country"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">City *</label>
            <input
              type="text"
              required
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
              placeholder="City"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">Contact Email *</label>
            <input
              type="email"
              required
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
              placeholder="contact@company.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">Contact Phone</label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
              placeholder="+1 234 567 8900"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">User ID (for verification)</label>
          <input
            type="text"
            value={formData.user_id}
            onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
            className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none font-mono text-sm"
            placeholder="user_abc123def456"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">Short Description</label>
          <textarea
            rows={2}
            value={formData.short_description}
            onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
            className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none resize-none"
            placeholder="Brief description of services"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">Service Locations</label>
          <div className="flex flex-wrap gap-2">
            {serviceLocations.map((loc) => (
              <button
                key={loc}
                type="button"
                onClick={() => handleMultiSelect('service_locations', loc)}
                className={`px-3 py-1 rounded-lg text-xs transition-all ${
                  formData.service_locations.includes(loc)
                    ? 'bg-[#6366F1] text-white'
                    : 'bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
                }`}
              >
                {loc}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">Industries Served</label>
          <div className="flex flex-wrap gap-2">
            {industries.map((ind) => (
              <button
                key={ind}
                type="button"
                onClick={() => handleMultiSelect('industries_served', ind)}
                className={`px-3 py-1 rounded-lg text-xs transition-all ${
                  formData.industries_served.includes(ind)
                    ? 'bg-[#6366F1] text-white'
                    : 'bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
                }`}
              >
                {ind}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">Services Offered</label>
          <div className="flex flex-wrap gap-2">
            {services.map((svc) => (
              <button
                key={svc}
                type="button"
                onClick={() => handleMultiSelect('services_offered', svc)}
                className={`px-3 py-1 rounded-lg text-xs transition-all ${
                  formData.services_offered.includes(svc)
                    ? 'bg-[#6366F1] text-white'
                    : 'bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
                }`}
              >
                {svc}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="published"
            checked={formData.published}
            onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
            className="w-4 h-4 rounded border-gray-600 text-[#6366F1] focus:ring-[#6366F1]"
          />
          <label htmlFor="published" className="text-sm text-white">
            Publish immediately (show in partner directory)
          </label>
        </div>
      </div>

      <div className="flex gap-3 pt-4 border-t border-[rgba(255,255,255,0.1)]">
        <button
          type="submit"
          disabled={processing}
          className="px-4 py-2 bg-[#6366F1] text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
        >
          {processing ? <CircleNotch size={16} className="animate-spin" /> : <CheckCircle size={16} />}
          Create Partner
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 glass text-white rounded-lg font-medium hover:bg-[rgba(255,255,255,0.1)]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Partner PencilSimple Form Component
const PartnerEditForm = ({ partner, onSave, onCancel, processing }) => {
  const [formData, setFormData] = useState({
    display_name: partner.display_name || partner.business_name,
    short_description: partner.short_description || '',
    contact_method: partner.contact_method || 'email',
    ai_compliance_acknowledged: partner.ai_compliance_acknowledged || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-xl font-bold text-white mb-4">Edit Partner Profile</h2>
      
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-white mb-2">Display Name</label>
          <input
            type="text"
            value={formData.display_name}
            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">Short Description</label>
          <textarea
            rows={3}
            value={formData.short_description}
            onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
            className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none resize-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">Contact Method</label>
          <select
            value={formData.contact_method}
            onChange={(e) => setFormData({ ...formData, contact_method: e.target.value })}
            className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none"
            style={{ colorScheme: 'dark' }}
          >
            <option value="email">Email</option>
            <option value="form">Contact Form</option>
          </select>
        </div>

        {/* Compliance & AI Use Section - Admin Only */}
        <div className="mt-6 pt-6 border-t border-[rgba(255,255,255,0.1)]">
          <div className="flex items-center gap-2 mb-4">
            <Shield size={18} className="text-[#6366F1]" />
            <h3 className="text-base font-semibold text-white">AI Use & Responsibility (Partner Guidance)</h3>
          </div>
          
          <div className="p-4 bg-[rgba(99,102,241,0.05)] border border-[rgba(99,102,241,0.15)] rounded-lg mb-4">
            <p className="text-sm text-[#A0AEC0] mb-4">
              Man and Machine AI provides decision-support tools powered by artificial intelligence.
            </p>
            
            <p className="text-sm font-medium text-white mb-2">Partners must ensure:</p>
            <ul className="text-sm text-[#A0AEC0] space-y-2 mb-4 list-disc list-inside">
              <li>AI outputs are reviewed by a human before use</li>
              <li>AI is not positioned as autonomous decision-making</li>
              <li>Clients understand AI outputs are assistive, not professional advice</li>
            </ul>
            
            <p className="text-xs text-[#6B7280] italic">
              Partners are responsible for ensuring AI use complies with applicable laws and industry regulations in their region.
            </p>
          </div>

          {/* Sales Do's and Don'ts */}
          <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg space-y-4">
            <h4 className="text-sm font-semibold text-white">AI Use & Sales Guidance</h4>
            
            {/* DO */}
            <div>
              <p className="text-xs font-medium text-green-400 mb-2">✅ DO — What Partners Can Say</p>
              <ul className="text-xs text-[#A0AEC0] space-y-1 list-none">
                <li>✔ "This AI helps analyse, summarise, and support decisions."</li>
                <li>✔ "AI assists teams by reducing manual work."</li>
                <li>✔ "Outputs are reviewed by humans before action."</li>
                <li>✔ "Man & Machine enables human-in-the-loop AI."</li>
                <li>✔ "The platform supports compliance-aware AI use."</li>
              </ul>
            </div>
            
            {/* DON'T */}
            <div>
              <p className="text-xs font-medium text-red-400 mb-2">❌ DON'T — What Partners Must Not Say</p>
              <ul className="text-xs text-[#A0AEC0] space-y-1 list-none">
                <li>✖ "The AI makes decisions for you."</li>
                <li>✖ "This replaces consultants / accountants / lawyers."</li>
                <li>✖ "The AI guarantees accuracy or compliance."</li>
                <li>✖ "This is autonomous or self-governing AI."</li>
                <li>✖ "The system removes the need for human oversight."</li>
              </ul>
            </div>
            
            {/* Warning */}
            <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded">
              <p className="text-xs text-amber-300">
                <strong>⚠️ Regulated Use Reminder:</strong> Partners must ensure AI is not marketed or deployed as an autonomous decision-making system. Final responsibility remains with the client.
              </p>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                checked={formData.ai_compliance_acknowledged}
                onChange={(e) => setFormData({ ...formData, ai_compliance_acknowledged: e.target.checked })}
                className="peer sr-only"
              />
              <div className={`w-5 h-5 rounded border-2 transition-colors flex items-center justify-center ${
                formData.ai_compliance_acknowledged 
                  ? 'bg-[#6366F1] border-[#6366F1]' 
                  : 'border-[rgba(255,255,255,0.3)] group-hover:border-[#6366F1]'
              }`}>
                {formData.ai_compliance_acknowledged && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm text-white">
              I understand and agree to position Man and Machine AI as decision-support technology, not autonomous intelligence
              <span className="text-red-400 ml-1">*</span>
            </span>
          </label>
          
          {!formData.ai_compliance_acknowledged && (
            <p className="text-xs text-amber-400 mt-2 ml-8">
              Partner profile cannot be published without acknowledging AI usage responsibilities.
            </p>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={processing || !formData.ai_compliance_acknowledged}
          className="px-4 py-2 bg-[#6366F1] text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          title={!formData.ai_compliance_acknowledged ? 'Please acknowledge AI usage responsibilities' : ''}
        >
          {processing ? <CircleNotch size={16} className="animate-spin" /> : <CheckCircle size={16} />}
          Save Changes
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 glass text-white rounded-lg font-medium hover:bg-[rgba(255,255,255,0.1)]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default AdminPartners;
